import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { MessageCircle, Check, Sparkles } from "lucide-react";
import { api, rp, fmtDate, statusColor } from "@/lib/api";
import { WinBack } from "@/admin/WinBack";

export default function Reminders() {
  const [d, setD] = useState(null);
  const [days, setDays] = useState(1);
  const [wa, setWa] = useState("");
  const [tiers, setTiers] = useState([]);

  const loadTiers = () => api.get("/tier-upgrades").then((r) => setTiers(r.data)).catch(() => setTiers([]));

  const load = (n) => api.get("/reminders", { params: { days_ahead: n } }).then((r) => setD(r.data));
  useEffect(() => { load(days); loadTiers(); api.get("/settings").then((r) => setWa(r.data.whatsapp_number)); }, [days]);

  const sendTier = async (t) => {
    window.open(t.whatsapp_url, "_blank");
    await api.post(`/tier-upgrades/${t.customer_id}/sent`);
    loadTiers(); toast.success("Ucapan naik tier dibuka di WhatsApp");
  };

  const send = async (b) => {
    window.open(`https://wa.me/${(b.customer_whatsapp || "").replace(/\D/g, "").replace(/^0/, "62")}?text=${encodeURIComponent(b.message)}`, "_blank");
    await api.post(`/bookings/${b.id}/reminder-sent`);
    load(days); toast.success("Reminder opened in WhatsApp");
  };

  if (!d) return <div className="text-ink-soft">Loading…</div>;

  return (
    <div data-testid="admin-reminders">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-3xl tracking-tight">Booking Reminders</h1>
          <p className="text-sm text-ink-soft mt-1">One-tap WhatsApp reminders for rentals on {fmtDate(d.rental_date)}</p>
        </div>
        <div className="flex gap-2">
          {[0, 1, 2].map((n) => (
            <button key={n} data-testid={`rem-days-${n}`} onClick={() => setDays(n)}
              className={`px-4 py-2 rounded-md text-sm border ${days === n ? "bg-ink text-cream border-ink" : "border-black/15"}`}>
              {n === 0 ? "Today" : n === 1 ? "Tomorrow" : `In ${n} days`}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-5 space-y-3">
        {d.bookings.map((b) => (
          <div key={b.id} className="bg-white border border-black/10 rounded-lg p-4" data-testid={`reminder-${b.booking_code}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0">
                <div className="text-sm font-semibold">{b.customer_name} · {b.customer_whatsapp}</div>
                <div className="text-xs text-ink-soft mt-0.5">
                  <Link to={`/admin/bookings/${b.id}`} className="text-lm-red">{b.booking_code}</Link> · {b.start_time} · {b.package_name_snapshot} · {b.quantity} unit · {rp(b.total_payment)}
                </div>
                <span className={`mt-2 inline-block text-[11px] px-2 py-0.5 rounded-full border ${statusColor(b.booking_status)}`}>{b.booking_status}</span>
                {b.reminder_sent_at && (
                  <span className="ml-2 text-[11px] text-emerald-700 inline-flex items-center gap-1">
                    <Check size={12} /> reminded {b.reminder_sent_at.slice(0, 16).replace("T", " ")}
                  </span>
                )}
              </div>
              <button data-testid={`send-reminder-${b.booking_code}`} onClick={() => send(b)}
                className="lm-btn-primary rounded-md px-4 py-2.5 text-sm font-semibold inline-flex items-center gap-2">
                <MessageCircle size={15} /> Send WhatsApp Reminder
              </button>
            </div>
            <pre className="mt-3 whitespace-pre-wrap font-sans text-xs text-ink-soft border-t border-black/5 pt-3">{b.message}</pre>
          </div>
        ))}
        {d.bookings.length === 0 && <div className="bg-white border border-black/10 rounded-lg p-5 text-sm text-ink-soft">No active rentals on this date.</div>}
      </div>

      <WinBack />
      <section className="mt-8" data-testid="tier-upgrades">
        <h2 className="text-base md:text-lg font-semibold flex items-center gap-2">
          <Sparkles size={16} className="text-lm-gold" /> Naik Member Tier
          {tiers.length > 0 && <span className="text-[11px] px-2 py-0.5 rounded-full bg-lm-red text-white" data-testid="tier-upgrade-count">{tiers.length}</span>}
        </h2>
        <p className="mt-1 text-sm text-ink-soft">Pelanggan yang baru naik tier — kirim ucapan sekali klik.</p>
        <div className="mt-3 space-y-3">
          {tiers.map((t) => (
            <div key={t.customer_id} className="bg-white border border-lm-gold/40 rounded-lg p-4" data-testid={`tier-up-${t.customer_id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <div className="text-sm font-semibold">{t.name} · {t.whatsapp}</div>
                  <div className="text-xs text-ink-soft mt-0.5">
                    {t.from_tier} → <span className="text-ink font-semibold">{t.to_tier}</span> · {t.total_rentals}x sewa · diskon {t.discount_percent}%
                  </div>
                </div>
                <button data-testid={`send-tier-${t.customer_id}`} onClick={() => sendTier(t)}
                  className="lm-btn-primary rounded-md px-4 py-2.5 text-sm font-semibold inline-flex items-center gap-2">
                  <MessageCircle size={15} /> Kirim Ucapan
                </button>
              </div>
              <pre className="mt-3 whitespace-pre-wrap font-sans text-xs text-ink-soft border-t border-black/5 pt-3">{t.message}</pre>
            </div>
          ))}
          {tiers.length === 0 && (
            <div className="bg-white border border-black/10 rounded-lg p-5 text-sm text-ink-soft" data-testid="tier-upgrades-empty">
              Belum ada pelanggan yang naik tier.
            </div>
          )}
        </div>
      </section>

      <p className="mt-5 text-xs text-ink-soft">
        Butuh template lain (sebelum antar, pengingat pengembalian, kendala unit)? Semua ada di{" "}
        <Link to="/admin/whatsapp" className="text-lm-red">WhatsApp Center</Link>. Template bisa diedit di Settings. Business WhatsApp: {wa}
      </p>
    </div>
  );
}
